import { computed, onUnmounted, ref, watch, type Ref } from 'vue';

export type BossFightSession = {
  runId: number;
  userId: number;
  turnStartedAt: string;
  durationMs: number;
  clicks: number;
  bonusAmounts: number[];
  submitted: boolean;
};

const STORAGE_PREFIX = 'hp_boss_fight_session_';

function storageKey(runId: number, userId: number, turnStartedAt: string) {
  return `${STORAGE_PREFIX}${runId}_${userId}_${turnStartedAt}`;
}

function normalizeSession(raw: Partial<BossFightSession> & { bonusHits?: number; fightStartedAt?: string }): BossFightSession | null {
  const turnStartedAt = raw.turnStartedAt ?? raw.fightStartedAt;
  if (
    typeof raw.runId !== 'number' ||
    typeof raw.userId !== 'number' ||
    typeof turnStartedAt !== 'string' ||
    typeof raw.durationMs !== 'number'
  ) {
    return null;
  }

  return {
    runId: raw.runId,
    userId: raw.userId,
    turnStartedAt,
    durationMs: raw.durationMs,
    clicks: raw.clicks ?? 0,
    bonusAmounts: Array.isArray(raw.bonusAmounts)
      ? raw.bonusAmounts.filter((amount) => amount > 0)
      : [],
    submitted: Boolean(raw.submitted),
  };
}

function loadSession(runId: number, userId: number, turnStartedAt: string): BossFightSession | null {
  try {
    const raw = localStorage.getItem(storageKey(runId, userId, turnStartedAt));
    if (!raw) return null;
    return normalizeSession(JSON.parse(raw) as Partial<BossFightSession> & { bonusHits?: number; fightStartedAt?: string });
  } catch {
    return null;
  }
}

function saveSession(session: BossFightSession) {
  localStorage.setItem(storageKey(session.runId, session.userId, session.turnStartedAt), JSON.stringify(session));
}

function clearSession(runId: number, userId: number, turnStartedAt: string) {
  localStorage.removeItem(storageKey(runId, userId, turnStartedAt));
}

type SessionContext = {
  runId: number | null;
  userId: number | undefined;
  turnStartedAt: string | null;
  durationMs: number;
  canAttack: boolean;
  canSubmitDamage: boolean;
  serverRemainingMs: number | null;
  myTurnSubmitted: boolean;
};

export function useBossFightSession(context: Ref<SessionContext>) {
  const session = ref<BossFightSession | null>(null);
  const remainingMs = ref(0);
  let timer: ReturnType<typeof setInterval> | null = null;
  let lastServerRemainingMs: number | null = null;
  let lastSyncAt = 0;

  function stopTimer() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function calibrateRemaining(serverMs: number | null) {
    if (serverMs === null || serverMs === undefined) {
      remainingMs.value = 0;
      return;
    }
    lastServerRemainingMs = serverMs;
    lastSyncAt = Date.now();
    remainingMs.value = Math.max(0, serverMs);
  }

  function tickRemaining() {
    if (lastServerRemainingMs === null) {
      remainingMs.value = 0;
      return;
    }
    const elapsed = Date.now() - lastSyncAt;
    remainingMs.value = Math.max(0, lastServerRemainingMs - elapsed);
  }

  function startTimer() {
    stopTimer();
    tickRemaining();
    timer = setInterval(tickRemaining, 200);
  }

  function syncSession() {
    const ctx = context.value;

    if (!ctx.userId || !ctx.runId || !ctx.turnStartedAt || ctx.myTurnSubmitted) {
      session.value = null;
      remainingMs.value = 0;
      lastServerRemainingMs = null;
      stopTimer();
      return;
    }

    const needsSession = ctx.canAttack || ctx.canSubmitDamage;
    if (!needsSession) {
      session.value = null;
      remainingMs.value = 0;
      lastServerRemainingMs = null;
      stopTimer();
      return;
    }

    const existing = loadSession(ctx.runId, ctx.userId, ctx.turnStartedAt);
    if (existing && !existing.submitted && existing.runId === ctx.runId && existing.turnStartedAt === ctx.turnStartedAt) {
      session.value = existing;
    } else {
      session.value = {
        runId: ctx.runId,
        userId: ctx.userId,
        turnStartedAt: ctx.turnStartedAt,
        durationMs: ctx.durationMs,
        clicks: 0,
        bonusAmounts: [],
        submitted: false,
      };
      saveSession(session.value);
    }

    if (ctx.canAttack) {
      calibrateRemaining(ctx.serverRemainingMs);
      startTimer();
    } else {
      remainingMs.value = 0;
      lastServerRemainingMs = null;
      stopTimer();
    }
  }

  watch(
    () => [
      context.value.runId,
      context.value.userId,
      context.value.turnStartedAt,
      context.value.durationMs,
      context.value.canAttack,
      context.value.canSubmitDamage,
      context.value.serverRemainingMs,
      context.value.myTurnSubmitted,
    ],
    syncSession,
    { immediate: true },
  );

  watch(
    () => context.value.serverRemainingMs,
    (serverMs) => {
      if (!context.value.canAttack || !session.value || session.value.submitted) return;
      calibrateRemaining(serverMs);
    },
  );

  onUnmounted(stopTimer);

  function addClick() {
    if (!session.value || session.value.submitted || !context.value.canAttack || remainingMs.value <= 0) return;
    session.value = { ...session.value, clicks: session.value.clicks + 1 };
    saveSession(session.value);
  }

  function addBonusAmount(amount: number) {
    if (!session.value || session.value.submitted || !context.value.canAttack || remainingMs.value <= 0 || amount <= 0) {
      return;
    }
    session.value = {
      ...session.value,
      bonusAmounts: [...session.value.bonusAmounts, amount],
    };
    saveSession(session.value);
  }

  function markSubmitted() {
    if (!session.value) return;
    const { runId, userId, turnStartedAt } = session.value;
    session.value = { ...session.value, submitted: true };
    clearSession(runId, userId, turnStartedAt);
    session.value = null;
    stopTimer();
    remainingMs.value = 0;
    lastServerRemainingMs = null;
  }

  const bonusTotal = computed(() =>
    (session.value?.bonusAmounts ?? []).reduce((sum, amount) => sum + amount, 0),
  );

  const isActive = computed(
    () => Boolean(context.value.canAttack && session.value && !session.value.submitted && remainingMs.value > 0),
  );

  const timerExpired = computed(
    () => Boolean(context.value.canAttack && session.value && !session.value.submitted && remainingMs.value <= 0),
  );

  const timerLabel = computed(() => {
    if (!context.value.canAttack) return '';
    const ms = remainingMs.value;
    const sec = Math.ceil(ms / 1000);
    const min = Math.floor(sec / 60);
    const rest = sec % 60;
    return `${min}:${String(rest).padStart(2, '0')}`;
  });

  const awaitingSubmit = computed(
    () => Boolean(context.value.canSubmitDamage && session.value && !session.value.submitted),
  );

  return {
    session,
    remainingMs,
    bonusTotal,
    isActive,
    timerExpired,
    awaitingSubmit,
    /** @deprecated use awaitingSubmit */
    awaitingEndTurn: awaitingSubmit,
    timerLabel,
    addClick,
    addBonusAmount,
    markSubmitted,
  };
}
